/* eslint-disable @stylistic/max-len */
import type {RefObject} from 'react';

/**
 * The `ExtensionIconProps` interface defines the properties for the ExtensionIcon component.
 */
interface ExtensionIconProps {
    /** CSS class to override default styling. */
    className?: string;
    /** Primary color of the icon. */
    color1?: string;
    /** Height of the icon. */
    height?: number | string;
    ref?: RefObject<SVGSVGElement>;
    /** Test ID for Cypress. */
    testId?: string;
    /** Width of the icon. */
    width?: number | string;
}

/**
 * Material Symbol "extension" — outlined, weight 300.
 * Puzzle piece icon for the Spiel (Game) category.
 *
 * @param props           The component props.
 * @param props.className CSS class override.
 * @param props.color1    Primary icon color.
 * @param props.height    Icon height.
 * @param props.ref       SVG element ref.
 * @param props.testId    Test identifier.
 * @param props.width     Icon width.
 * @returns A React SVG element.
 */
export const ExtensionIcon = ({
    className = '',
    color1 = 'currentColor',
    height,
    ref,
    testId = 'ExtensionIcon',
    width
}: ExtensionIconProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        fill={color1}
        height={height}
        viewBox="0 -960 960 960"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M357.92-140H185.39q-18.15 0-31.77-13.62Q140-167.24 140-185.39v-172.53q38.23-8.47 64.35-37 26.11-28.54 26.11-67.31t-26.11-67.12q-26.12-28.34-64.35-37.19v-172.54q0-18.14 13.62-31.76 13.62-13.62 31.77-13.62h172.38q13.31-36.54 41.04-59.88 27.73-23.35 64.84-23.35 37.12 0 64.85 23.35 27.73 23.34 41.04 59.88h169.54q18.14 0 31.76 13.62 13.62 13.62 13.62 31.76v169.54q36.54 13.31 58.77 42.46 22.23 29.16 22.23 66.27 0 37.12-22.23 63.73-22.23 26.62-58.77 39.93v171.76q0 18.15-13.62 31.77Q757.22-140 739.08-140H566.54q-9.23-41.46-38.48-65.96-29.24-24.5-65.92-24.5-36.68 0-66.02 24.5-29.35 24.5-38.2 65.96Zm-172.53-45.39h141.53q21.16-49.07 59.58-69.76 38.41-20.7 75.69-20.7 37.27 0 75.73 20.7 38.46 20.69 59.62 69.76h141.54v-214.22h23.46q27.3-2.31 42.42-19.93 15.12-17.61 15.12-41.08 0-23.46-15.12-41.27-15.12-17.8-42.42-20.11h-23.46v-217.08H524.85v-25.69q-2.31-27.31-19.93-42.42-17.61-15.12-41.27-15.12-23.65 0-41.27 15.12-17.61 15.11-19.92 42.42v25.69H185.39v140.39q41.61 17.05 66.03 54.38 24.43 37.33 24.43 82.3 0 44.44-24.5 81.92-24.5 37.47-65.96 54.32v140.38Zm278.46-275.23Z" />
    </svg>
);

ExtensionIcon.displayName = 'ExtensionIcon';